import type { ComponentType } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ROUTES } from '@/lib/routes';
import { buildGeneratedCampaignName } from './lib/campaign-name';
import { useWizardStore } from './lib/wizard-store';
import { W1_ChannelsStrategy } from './steps/W1_ChannelsStrategy';
import { W2_SenderOfferTarget } from './steps/W2_SenderOfferTarget';
import { W3_TargetEligibility } from './steps/W3_TargetEligibility';
import { W4_OutreachAgents } from './steps/W4_OutreachAgents';
import { W4_MailProviderAccounts } from './steps/W4_MailProviderAccounts';
import { W3_EmailTemplates } from './steps/W3_EmailTemplates';
import { W4_FormFillMessage } from './steps/W4_FormFillMessage';
import { W5_ReplyForwarder } from './steps/W5_ReplyForwarder';
import { W6_MailAssistantKnowledge } from './steps/W6_MailAssistantKnowledge';
import { W6_BigcallSync } from './steps/W6_BigcallSync';
import { W7_Enrichisseurs } from './steps/W7_Enrichisseurs';
import { W8_Summary } from './steps/W8_Summary';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function buildSteps(channelsEnabled: string[]) {
  const steps: { id: number; label: string; component: ComponentType }[] = [
    { id: 1, label: 'Channels & strategie', component: W1_ChannelsStrategy },
    { id: 2, label: 'Expediteur & offre', component: W2_SenderOfferTarget },
    { id: 3, label: 'Territoire & eligibilite', component: W3_TargetEligibility },
  ];

  if (channelsEnabled.includes('mail')) {
    steps.push({ id: 4, label: 'Providers & comptes', component: W4_MailProviderAccounts });
    steps.push({ id: 5, label: 'Templates email', component: W3_EmailTemplates });
  }

  if (channelsEnabled.includes('linkedin_outreach')) {
    steps.push({ id: 6, label: 'Agents Outreach', component: W4_OutreachAgents });
  }

  if (channelsEnabled.includes('formfill')) {
    steps.push({ id: 6, label: 'Message form-fill', component: W4_FormFillMessage });
  }

  if (channelsEnabled.includes('mail')) {
    steps.push({ id: 7, label: 'Reply-To & forwarder', component: W5_ReplyForwarder });
    steps.push({ id: 8, label: 'AI knowledge & policy', component: W6_MailAssistantKnowledge });
  }

  if (channelsEnabled.includes('call')) {
    steps.push({ id: 9, label: 'Sync BIG.call', component: W6_BigcallSync });
  }

  steps.push({ id: 10, label: 'Enrichisseurs', component: W7_Enrichisseurs });
  steps.push({ id: 11, label: 'Recapitulatif', component: W8_Summary });
  return steps;
}

export default function NewCampaignWizard() {
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const {
    channels_enabled,
    sender_company_name,
    sender_first_name,
    sender_email,
    campaign_identifier,
    offer_title,
    target_country,
    language,
    mail_providers,
    mail_provider_email_account_ids,
    email_templates,
    bigcall_workspace_id,
    leadscrape_targeting,
    fit_gate,
    syncGeneratedCampaignName,
  } = useWizardStore();

  const steps = buildSteps(channels_enabled);
  const StepComponent = steps[step]!.component;
  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const generatedCampaignName = useMemo(
    () =>
      buildGeneratedCampaignName({
        targetCountry: target_country,
        language,
        leadscrapeIndustries: leadscrape_targeting.industries,
        preferredSectors: fit_gate.preferred_sectors,
        campaignIdentifier: campaign_identifier,
        offerTitle: offer_title,
        senderCompanyName: sender_company_name,
      }),
    [
      campaign_identifier,
      fit_gate.preferred_sectors,
      language,
      leadscrape_targeting.industries,
      offer_title,
      sender_company_name,
      target_country,
    ],
  );

  useEffect(() => {
    syncGeneratedCampaignName(generatedCampaignName);
  }, [generatedCampaignName, syncGeneratedCampaignName]);

  const hasFormfill = channels_enabled.includes('formfill');
  const canAdvance = (() => {
    if (StepComponent === W1_ChannelsStrategy) return channels_enabled.length > 0;
    if (StepComponent === W2_SenderOfferTarget) {
      if (sender_company_name.trim().length === 0) return false;
      if (target_country.trim().length === 0) return false;
      if (hasFormfill) {
        if (sender_first_name.trim().length === 0) return false;
        if (!EMAIL_RE.test(sender_email)) return false;
      }
      return true;
    }
    if (StepComponent === W4_MailProviderAccounts) {
      return mail_providers.length > 0 && mail_provider_email_account_ids.length > 0;
    }
    if (StepComponent === W3_EmailTemplates) return email_templates.email01.trim().length > 0;
    if (StepComponent === W6_BigcallSync) return bigcall_workspace_id.trim().length > 0;
    return true;
  })();

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 lg:px-6 xl:px-8 space-y-6">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>Nouvelle campagne</span>
        <span>&gt;</span>
        <span className="font-medium text-gray-800">
          Etape {step + 1}/{steps.length} - {steps[step]!.label}
        </span>
      </div>

      <StepComponent />

      <div className="flex justify-between">
        <Button
          variant="secondary"
          onClick={isFirst ? () => navigate(ROUTES.CAMPAIGNS) : () => setStep(step - 1)}
        >
          {isFirst ? 'Annuler' : 'Precedent'}
        </Button>
        {!isLast && (
          <Button
            variant="primary"
            disabled={!canAdvance}
            onClick={() => setStep(step + 1)}
            data-testid="next-button"
          >
            Suivant
          </Button>
        )}
      </div>
    </div>
  );
}
